import type { Feature, FeatureStatus } from "../tracking/Tracking";
import type { TrackingLabels } from "../tracking/TrackingLabels";
import {
  deriveParentStatusFromChildren,
  fullStatus,
  readInitiallyChecked,
  trackedFilterMethod,
} from "./FilterTracking";

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */

const OVERVIEW_ID_FALLBACK = "find_filterSection";

/*                                                                                              */
export interface ValueSnapshot {
  id: string;
  parentId: string;
  position?: number;
  initiallyChecked: boolean;
  currentlyChecked: boolean;
  /*                                                                                         */
  labels: Feature["labels"];
  /*                                                                */
  filterMethod?: TrackingLabels["san_FilterMethod"];
}

export interface FacetSnapshot {
  id: string;
  parentId: string;
  position?: number;
  labels: Feature["labels"];
  values: ValueSnapshot[];
  /*                                                                                                          */
  baseStatus: FeatureStatus;
}

export interface FilterSnapshot {
  id: string;
  position?: number;
  labels: Feature["labels"];
  facets: FacetSnapshot[];
  /*                                                                                                          */
  baseStatus: FeatureStatus;
  /*                                                                                     */
  changeLabels?: Partial<Record<keyof TrackingLabels, string | string[]>>;
}

export interface SectionSnapshot {
  overviewId: string;
  overviewLabels: Feature["labels"];
  filters: FilterSnapshot[];
}

/*                                                                                      */
function projectToSubmit(full: FeatureStatus): FeatureStatus {
  if (full === "preselected") return "added";
  if (full === "predeselected") return "deleted";
  return full;
}

/*                                                                                                  */
function resolveParentStatus(derived: FeatureStatus, baseStatus: FeatureStatus): FeatureStatus {
  return derived === "visible" ? baseStatus : projectToSubmit(derived);
}

/*                                                                                             */
function isChangedStatus(full: FeatureStatus): boolean {
  return full === "preselected" || full === "predeselected";
}

/*                                                                                                     */
function isExcludedValueStatus(status: FeatureStatus): boolean {
  return status === "visible" || status === "loaded";
}

/**
 *
 *
 *
 *
 *
 *
 *
 *
 */
function buildValueLabels(
  value: ValueSnapshot,
  full: FeatureStatus,
  changeLabels?: FilterSnapshot["changeLabels"],
): Feature["labels"] {
  const labels: Record<string, unknown> = { ...(value.labels ?? {}) };
  delete labels.san_FilterMethod;
  if (changeLabels) {
    for (const key of Object.keys(changeLabels)) {
      delete labels[key];
    }
  }
  if (!isChangedStatus(full)) {
    return labels as Feature["labels"];
  }
  if (value.filterMethod) {
    labels.san_FilterMethod = [value.filterMethod];
  }
  if (changeLabels) {
    for (const [key, raw] of Object.entries(changeLabels)) {
      labels[key] = Array.isArray(raw) ? raw : [raw];
    }
  }
  return labels as Feature["labels"];
}

function withOptional(position?: number, parentId?: string): Pick<Feature, "position" | "parentId"> {
  return {
    ...(position !== undefined && { position }),
    ...(parentId !== undefined && { parentId }),
  };
}

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export function assembleFilterFeatures(section: SectionSnapshot): Feature[] {
  const features: Feature[] = [];

  features.push({
    id: section.overviewId,
    name: "Filter-Overview",
    status: "visible",
    labels: section.overviewLabels ?? {},
  });

  /*                                                                                      */
  /*                                                                                                 */
  const valueFull = new Map<string, FeatureStatus>();
  for (const filter of section.filters) {
    for (const facet of filter.facets) {
      for (const value of facet.values) {
        valueFull.set(value.id, fullStatus(value.initiallyChecked, value.currentlyChecked));
      }
    }
  }

  /*                                                                                                 */
  /*                                                                    */
  for (const filter of section.filters) {
    const leafStatuses = filter.facets.flatMap((facet) =>
      facet.values.map((v) => valueFull.get(v.id) as FeatureStatus),
    );
    features.push({
      id: filter.id,
      name: "Filter",
      status: resolveParentStatus(deriveParentStatusFromChildren(leafStatuses), filter.baseStatus),
      ...withOptional(filter.position, section.overviewId),
      labels: filter.labels ?? {},
    });
  }

  /*                                                        */
  for (const filter of section.filters) {
    for (const facet of filter.facets) {
      const facetFull = deriveParentStatusFromChildren(facet.values.map((v) => valueFull.get(v.id) as FeatureStatus));
      features.push({
        id: facet.id,
        name: "Facet",
        status: resolveParentStatus(facetFull, facet.baseStatus),
        ...withOptional(facet.position, filter.id),
        labels: facet.labels ?? {},
      });
    }
  }

  /*                                                                                               */
  for (const filter of section.filters) {
    for (const facet of filter.facets) {
      for (const value of facet.values) {
        const full = valueFull.get(value.id) as FeatureStatus;
        const status = projectToSubmit(full);
        if (isExcludedValueStatus(status)) {
          continue;
        }
        features.push({
          id: value.id,
          name: "Facet-Value",
          status,
          ...withOptional(value.position, facet.id),
          labels: buildValueLabels(value, full, filter.changeLabels),
        });
      }
    }
  }

  return features;
}

function parsePosition(raw?: string): number | undefined {
  return raw !== undefined ? parseInt(raw, 10) : undefined;
}

function parseLabels(raw?: string): Feature["labels"] {
  return JSON.parse(raw ?? "{}") as Feature["labels"];
}

/*                                                                                                               */
function parseBaseStatus(raw?: string): FeatureStatus {
  return (raw || "visible") as FeatureStatus;
}

/**
 *
 *
 *
 *
 *
 *
 */
export function snapshotFilterDom(root: ParentNode = document): SectionSnapshot {
  const overview = root.querySelector<HTMLElement>('[data-ts-feature-name="Filter-Overview"]');

  const valuesByFacet = new Map<string, ValueSnapshot[]>();
  root.querySelectorAll<HTMLInputElement>('[data-ts-feature-name="Facet-Value"]').forEach((input) => {
    const parentId = input.dataset.tsFeatureParentId;
    if (!parentId) return;
    const value: ValueSnapshot = {
      id: input.id,
      parentId,
      position: parsePosition(input.dataset.tsFeaturePosition),
      initiallyChecked: readInitiallyChecked(input),
      currentlyChecked: input.checked,
      labels: parseLabels(input.dataset.tsFeatureLabels),
      filterMethod: trackedFilterMethod(input),
    };
    const bucket = valuesByFacet.get(parentId);
    if (bucket) {
      bucket.push(value);
    } else {
      valuesByFacet.set(parentId, [value]);
    }
  });

  const facetsByFilter = new Map<string, FacetSnapshot[]>();
  root.querySelectorAll<HTMLElement>('[data-ts-feature-name="Facet"]').forEach((elem) => {
    const parentId = elem.dataset.tsFeatureParentId;
    if (!parentId) return;
    const facet: FacetSnapshot = {
      id: elem.id,
      parentId,
      position: parsePosition(elem.dataset.tsFeaturePosition),
      labels: parseLabels(elem.dataset.tsFeatureLabels),
      values: valuesByFacet.get(elem.id) ?? [],
      baseStatus: parseBaseStatus(elem.dataset.tsFeatureStatus),
    };
    const bucket = facetsByFilter.get(parentId);
    if (bucket) {
      bucket.push(facet);
    } else {
      facetsByFilter.set(parentId, [facet]);
    }
  });

  const filters: FilterSnapshot[] = [];
  root.querySelectorAll<HTMLElement>('[data-ts-feature-name="Filter"]').forEach((elem) => {
    filters.push({
      id: elem.id,
      position: parsePosition(elem.dataset.tsFeaturePosition),
      labels: parseLabels(elem.dataset.tsFeatureLabels),
      facets: facetsByFilter.get(elem.id) ?? [],
      changeLabels: elem.dataset.tsChangeLabels ? JSON.parse(elem.dataset.tsChangeLabels) : undefined,
      baseStatus: parseBaseStatus(elem.dataset.tsFeatureStatus),
    });
  });

  return {
    overviewId: overview?.id ?? OVERVIEW_ID_FALLBACK,
    overviewLabels: overview ? parseLabels(overview.dataset.tsFeatureLabels) : {},
    filters,
  };
}
